import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar, Box, Button, Chip, CircularProgress, IconButton, Stack,
    TextField, Tooltip, Typography,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import { getChat, postChat, moderateChat } from '../../api/eventApi';
import { fileUrl } from '../../utils/fileUrl';

const FONT = "'Poppins', sans-serif";
const POLL_MS = 3000;

/* ============================================================================
   TRAINING ROOM CHAT

   Transport is a polling CURSOR, not a WebSocket: each tick asks for messages
   after the highest id already held, so it answers with [] almost every time and
   needs no nginx upgrade block on prod. Same pattern as the notification bell,
   which polls on 30s — this one is faster because people are typing at each other,
   and it only runs while the page is open.

   The body is rendered as TEXT. This is the first place in the system where one
   agent's typing reaches every other agent, so it must never touch
   dangerouslySetInnerHTML.

   Chat belongs to the SERIES, stamped with the session. "This week" is the default
   view; the toggle widens it to every past question and its answer — which is the
   point of running the same training every week. (user 2026-10-03)
   ========================================================================= */
export default function EventChat({ eventId, eventSessionId, canManage, meId }) {
    const [messages, setMessages] = useState([]);
    const [allSessions, setAllSessions] = useState(false);
    const [draft, setDraft] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const afterIdRef = useRef(0);
    const scrollRef = useRef(null);
    const atBottomRef = useRef(true);

    // Widening the window means re-reading from the start, so the cursor resets with it.
    useEffect(() => {
        afterIdRef.current = 0;
        setMessages([]);
        setLoading(true);
    }, [allSessions, eventId, eventSessionId]);

    const pull = useCallback(async () => {
        try {
            const res = await getChat({
                eventId,
                eventSessionId,
                afterId: afterIdRef.current,
                allSessions,
            });
            const incoming = res.data || [];
            if (incoming.length > 0) {
                afterIdRef.current = Math.max(...incoming.map(m => m.eventChatMessageId));
                setMessages(prev => {
                    // Merge rather than append: a moderated message comes back with the same
                    // id and has to replace its earlier copy, not sit beside it.
                    const byId = new Map(prev.map(m => [m.eventChatMessageId, m]));
                    incoming.forEach(m => byId.set(m.eventChatMessageId, m));
                    return [...byId.values()].sort((a, b) => a.eventChatMessageId - b.eventChatMessageId);
                });
            }
            setError('');
        } catch {
            // A dropped tick is not worth showing — the next one is three seconds away.
        } finally {
            setLoading(false);
        }
    }, [eventId, eventSessionId, allSessions]);

    useEffect(() => {
        pull();
        const t = setInterval(pull, POLL_MS);
        return () => clearInterval(t);
    }, [pull]);

    // Follow new messages only when the reader is already at the bottom, so arriving
    // chatter can't yank the page away from somebody reading back.
    useEffect(() => {
        const el = scrollRef.current;
        if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    };

    const send = async () => {
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            await postChat({ eventId, eventSessionId, body, parentMessageId: replyTo?.eventChatMessageId });
            setDraft('');
            setReplyTo(null);
            atBottomRef.current = true;
            await pull();
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not post that.');
        } finally {
            setSending(false);
        }
    };

    const moderate = async (m, patch) => {
        try {
            await moderateChat({ eventChatMessageId: m.eventChatMessageId, ...patch });
            // The cursor only ever moves forward, so a change to an existing message needs a
            // full re-read to come back.
            afterIdRef.current = 0;
            setMessages([]);
            await pull();
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not do that.');
        }
    };

    // Pinned first, then chronological. Replies hang under their parent.
    const threads = useMemo(() => {
        const roots = messages.filter(m => !m.parentMessageId);
        const repliesByParent = new Map();
        messages.filter(m => m.parentMessageId).forEach(m => {
            const list = repliesByParent.get(m.parentMessageId) || [];
            list.push(m);
            repliesByParent.set(m.parentMessageId, list);
        });
        return [...roots]
            .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0) || a.eventChatMessageId - b.eventChatMessageId)
            .map(root => ({ root, replies: repliesByParent.get(root.eventChatMessageId) || [] }));
    }, [messages]);

    const openQuestions = threads.filter(t => !t.root.isAnswered && !t.root.isFromOrganiser && !t.root.isDeleted).length;

    const Message = ({ m, isReply }) => (
        <Box sx={{ display: 'flex', gap: 1.25, ml: isReply ? 4.5 : 0, mb: 1.25 }}>
            <Avatar src={fileUrl(m.profilePhotoUrl)} sx={{ width: isReply ? 26 : 32, height: isReply ? 26 : 32, fontSize: '0.75rem' }}>
                {(m.agentName || '?').charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.78rem', color: '#0F172A' }}>
                        {m.agentName || 'Unknown'}
                    </Typography>
                    {m.isFromOrganiser && (
                        <Chip label="Trainer" size="small"
                            sx={{ height: 17, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#0E8FD1', color: '#fff' }} />
                    )}
                    {m.isPinned && <PushPinIcon sx={{ fontSize: 13, color: '#B45309' }} />}
                    {m.isAnswered && <CheckCircleIcon sx={{ fontSize: 13, color: '#059669' }} />}
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.66rem', color: '#94A3B8' }}>
                        {new Date(m.createdOn).toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                    </Typography>
                </Stack>

                {/* TEXT, never HTML. */}
                <Typography sx={{
                    fontFamily: FONT, fontSize: '0.82rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    color: m.isDeleted ? '#94A3B8' : '#1E293B', fontStyle: m.isDeleted ? 'italic' : 'normal',
                }}>
                    {m.isDeleted ? 'This message was removed.' : m.body}
                </Typography>

                {!m.isDeleted && (
                    <Stack direction="row" spacing={0.25} sx={{ mt: 0.25 }}>
                        {!isReply && (
                            <Tooltip title="Reply">
                                <IconButton size="small" onClick={() => setReplyTo(m)}>
                                    <ReplyIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canManage && !isReply && (
                            <>
                                <Tooltip title={m.isPinned ? 'Unpin' : 'Pin to the top'}>
                                    <IconButton size="small" onClick={() => moderate(m, { isPinned: !m.isPinned })}>
                                        {m.isPinned
                                            ? <PushPinIcon sx={{ fontSize: 15, color: '#B45309' }} />
                                            : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={m.isAnswered ? 'Mark unanswered' : 'Mark answered'}>
                                    <IconButton size="small" onClick={() => moderate(m, { isAnswered: !m.isAnswered })}>
                                        {m.isAnswered
                                            ? <CheckCircleIcon sx={{ fontSize: 15, color: '#059669' }} />
                                            : <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        {(canManage || m.userId === meId) && (
                            <Tooltip title="Remove">
                                <IconButton size="small" onClick={() => moderate(m, { del: true })}>
                                    <DeleteOutlineIcon sx={{ fontSize: 15, color: '#DC2626' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
                    Chat
                </Typography>
                {/* A work count, so it belongs to whoever has to do the work. An attendee
                    was being told "1 unanswered" about a queue they cannot act on - they
                    have no Mark answered button - which reads as the room being neglected.
                    (user 2026-10-04) */}
                {canManage && openQuestions > 0 && (
                    <Chip label={`${openQuestions} unanswered`} size="small"
                        sx={{ height: 19, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => setAllSessions(v => !v)}
                    sx={{ fontFamily: FONT, textTransform: 'none', fontSize: '0.72rem' }}>
                    {allSessions ? 'This session only' : 'All past questions'}
                </Button>
            </Stack>

            <Box ref={scrollRef} onScroll={onScroll}
                sx={{
                    // minHeight 0, not 220: inside a column capped to the viewport a hard
                    // floor here would push the message box off the bottom, which is the
                    // very thing being fixed. flex keeps it filling whatever is left.
                    flex: 1, minHeight: 0, overflowY: 'auto', p: 1.25,
                    border: '1px solid #E2E8F0', borderRadius: '10px', bgcolor: '#FBFDFF',
                }}>
                {loading && messages.length === 0 && (
                    <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={20} /></Stack>
                )}
                {!loading && threads.length === 0 && (
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: '#94A3B8', textAlign: 'center', py: 3 }}>
                        No questions yet
                    </Typography>
                )}
                {threads.map(({ root, replies }) => (
                    <Box key={root.eventChatMessageId} sx={{
                        mb: 1, p: root.isPinned ? 1 : 0,
                        borderRadius: root.isPinned ? '8px' : 0,
                        bgcolor: root.isPinned ? '#FFFBEB' : 'transparent',
                    }}>
                        <Message m={root} />
                        {replies.map(r => <Message key={r.eventChatMessageId} m={r} isReply />)}
                    </Box>
                ))}
            </Box>

            {replyTo && (
                <Stack direction="row" alignItems="center" spacing={1}
                    sx={{ mt: 1, px: 1, py: 0.5, bgcolor: '#EFF6FF', borderRadius: '8px' }}>
                    <ReplyIcon sx={{ fontSize: 14, color: '#0E8FD1' }} />
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#0E8FD1', flex: 1 }}>
                        Replying to {replyTo.agentName}
                    </Typography>
                    <Button size="small" onClick={() => setReplyTo(null)}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontSize: '0.7rem' }}>
                        Cancel
                    </Button>
                </Stack>
            )}

            {error && (
                <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#DC2626', mt: 0.75 }}>{error}</Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                    fullWidth size="small" multiline maxRows={4}
                    placeholder={canManage ? 'Answer a question…' : 'Ask a question…'}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                        // Enter sends; Shift+Enter is a new line.
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    inputProps={{ maxLength: 2000 }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: FONT, fontSize: '0.82rem' } }}
                />
                <Button variant="contained" onClick={send} disabled={sending || !draft.trim()}
                    sx={{ fontFamily: FONT, textTransform: 'none', minWidth: 44 }}>
                    {sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                </Button>
            </Stack>
        </Box>
    );
}
